import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/web/frontend/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/web/frontend/markdoc/tags.js'
import * as nodes from '/vercel/path0/web/frontend/markdoc/nodes.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer({"allowComments":true});

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Quickstart\n---\n\n## Step 1: Grant access\n\nGrant dbt's role access to the `snowflake` database:\n\n```sql\ngrant imported privileges on database snowflake to role your_dbt_role_name;\n```\n\n## Step 2: Update packages.yml\n\nAdd the package to your `packages.yml` file:\n\n```yaml\npackages:\n  - package: get-select/dbt_snowflake_monitoring\n    version: [\">=5.0.0\", \"<6.0.0\"] # We'll never make a breaking change without creating a new major version.\n```\n\n## Step 3: Enable query tags\n\n3. To attribute costs to individual models via the `dbt_metadata` column in the `query_history_enriched` model, query comments and tags are added to all dbt-issued queries. Both query comments and tags are needed to collect the required metadata for the `dbt_queries` model. First, make sure to remove any existing `+query_tag: dbt` from your `dbt_project.yml`, then add the query tags with one of these two options:\n\nOption 1: If running dbt >= 1.2, simply configure the dispatch search order in `dbt_project.yml`.\n\n```yaml\ndispatch:\n  - macro_namespace: dbt\n    search_order:\n      - <YOUR_PROJECT_NAME>\n      - dbt_snowflake_monitoring\n      - dbt\n```\n\nOption 2: If running dbt < 1.2, create a folder named `macros` in your dbt project's top level directory (if it doesn't exist). Inside, make a new file called `query_tags.sql` with the following content:\n\n{% markdoc-example %}\n\n```sql {% process=false %}\n{% macro set_query_tag() -%}\n{% do return(dbt_snowflake_monitoring.set_query_tag()) %}\n{% endmacro %}\n\n{% macro unset_query_tag(original_query_tag) -%}\n{% do return(dbt_snowflake_monitoring.unset_query_tag(original_query_tag)) %}\n{% endmacro %}\n```\n\n{% /markdoc-example %}\n\n## Step 4: Enable query comments\n4. To configure the query comments, add the following config to `dbt_project.yml`.\n\n```yaml\nquery-comment:\n  comment: '{{ dbt_snowflake_monitoring.get_query_comment(node) }}'\n  append: true # Snowflake removes prefixed comments.\n```\n\nThat's it! All dbt-issued queries will now be tagged and start appearing in the `dbt_queries.sql` model.\n\n## Building the models\n\nBy default, the models in this package will automatically get built during any full run of your dbt project. If you would like to manually create the models, or run them on a separate schedule, you can do so using [dbt's node selection syntax](https://docs.getdbt.com/reference/node-selection/syntax):\n\n`dbt run -s dbt_snowflake_monitoring`\n\n## dbt Cloud URLs\n\nIf you're using dbt Cloud, columns `dbt_cloud_job_url` and `dbt_cloud_run_url` can be configured in the `dbt_queries` model. To do so, set the variable `dbt_cloud_account_id`. This id can be retrieved from in between `/deploy/` and `/projects/` in any dbt Cloud URL.\n\nBy default, the URL prefix of `https://cloud.getdbt.com/deploy/` is used. If you're using a different region of dbt Cloud, this prefix can be overridden by specifying the `dbt_cloud_url` variable.\n\n\n## Troubleshooting\n\n### No data populated in the tables\n\nIf you aren't seeing data populated in the tables (example [here](https://github.com/get-select/dbt-snowflake-monitoring/issues/145)), it is possible that your account is missing `ORGADMIN` access. Please see [here](/docs/snowflake-metadata-access#step-5-ensure-your-account-has-orgadmin-access) for instructions on enabling `ORGADMIN` access in your Snowflake account.";
const filepath = "/docs/dbt-snowflake-monitoring/quickstart.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens, {slots: false});

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

async function getMarkdocData(context = {}) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  // Removes undefined
  return JSON.parse(
    JSON.stringify({
      content,
      frontmatter,
      file: {
        path: filepath,
      },
    })
  );
}

export async function getStaticProps(context) {
  return {
    props: {
      markdoc: await getMarkdocData(context),
    },
  };
}

export default function MarkdocComponent(props) {
  const markdoc = props.markdoc;
  // Only execute HMR code in development
  return renderers.react(markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
